import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AuthEntity } from '_entities';

import * as Styled from '../styles';
import DrawerItem from './DrawerItem';

const { getAccountProductInfo } = AuthEntity.selectors;

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Drawer = ({ sections, currentPathname, collectCount, onClose }) => {
  const history = useHistory();
  const accountProduct = useSelector(getAccountProductInfo);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const handleSelectSection = useCallback(
    (section) => {
      if (section.path) {
        history.push(section.path);
        onClose();
      }
    },
    [history, onClose],
  );

  const handleSelectSubRoute = useCallback(
    (sub) => {
      if (sub?.path) {
        history.push(sub.path);
        onClose();
      }
    },
    [history, onClose],
  );

  const badgeFor = (section) => {
    if (section.title === 'collect' && collectCount > 0) return collectCount;
    return null;
  };

  return (
    <Styled.DrawerBackdrop onClick={handleBackdropClick} role="presentation">
      <Styled.DrawerPanel role="dialog" aria-label="Navigation" aria-modal="true">
        <Styled.DrawerHeader>
          <Styled.DrawerBrand>
            <Styled.DrawerBrandLine1>Counter Tools</Styled.DrawerBrandLine1>
            {accountProduct && <Styled.DrawerBrandLine2>{accountProduct}</Styled.DrawerBrandLine2>}
          </Styled.DrawerBrand>
          <Styled.DrawerCloseButton type="button" onClick={onClose} aria-label="Close navigation">
            <CloseIcon />
          </Styled.DrawerCloseButton>
        </Styled.DrawerHeader>

        <Styled.DrawerList>
          {sections.map((section) => (
            <DrawerItem
              key={section.id ?? section.title}
              section={section}
              currentPathname={currentPathname}
              badge={badgeFor(section)}
              onSelectSection={handleSelectSection}
              onSelectSubRoute={handleSelectSubRoute}
            />
          ))}
        </Styled.DrawerList>
      </Styled.DrawerPanel>
    </Styled.DrawerBackdrop>
  );
};

Drawer.propTypes = {
  sections: PropTypes.array.isRequired,
  currentPathname: PropTypes.string.isRequired,
  collectCount: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

Drawer.defaultProps = {
  collectCount: 0,
};

export default Drawer;
